import { useCallback, useMemo, useState } from 'react'
import { Descendant } from 'slate'

import { RichTextEditor } from '@/features/view-tfl-uploaded/components/RichTextEditor'
import {
  initialEditorValue,
  serializeToMarkdown,
  serializeToPlainText,
} from '@/features/view-tfl-uploaded/utils/slateUtils.ts'
import { cn } from '@/utils'
import { DEFAULT_MAX_CHARACTERS } from '@/utils/constants.ts'

import { EditorMode, REPLY_EDITOR_ID } from '../../constants'

export interface ThreadReplyFooterProps {
  onSubmit: (markdownContent: string) => Promise<void> | void
  isSubmitting?: boolean
  disabled?: boolean
  placeholder?: string
  isQuoted?: boolean // whether the reply is quoted
}

export const ThreadReplyFooter: React.FC<ThreadReplyFooterProps> = ({
  onSubmit,
  isSubmitting = false,
  disabled = false,
  placeholder = 'Reply',
  isQuoted = false,
}) => {
  const [editorValue, setEditorValue] =
    useState<Descendant[]>(initialEditorValue)
  const [isComposing, setIsComposing] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  // Validation: disable Send when empty or exceeds character limit
  const canSend = useMemo(() => {
    if (disabled) return false

    const plain = serializeToPlainText(editorValue).trim()
    if (plain.length === 0 && !isComposing) return false
    if (plain.length > DEFAULT_MAX_CHARACTERS) return false

    if (!/[a-zA-Z0-9]/.test(plain)) return false

    return true
  }, [editorValue, isComposing, disabled])

  const resetEditor = useCallback(() => {
    setEditorValue(initialEditorValue)
    setIsComposing(false)
    setResetKey((k) => k + 1)
  }, [])

  const handleSend = useCallback(async () => {
    if (!canSend || isSubmitting || disabled) return

    const markdownContent = serializeToMarkdown(editorValue)
    resetEditor()
    onSubmit(markdownContent)
  }, [canSend, isSubmitting, disabled, editorValue, onSubmit, resetEditor])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isModifierPressed = event.metaKey || event.ctrlKey
      const isEnter = event.key === 'Enter'

      if (!isModifierPressed || !isEnter) {
        return
      }

      if (!canSend || isSubmitting || disabled) return
      event.preventDefault()
      handleSend()
    },
    [canSend, isSubmitting, disabled, handleSend]
  )

  if (disabled) {
    // Optional resolved-state message – tweak copy if needed
    return (
      <div className="border-t border-gray-200 px-5 pb-5 pt-3 text-xs text-gray-500">
        This thread is resolved. You can’t add new replies.
      </div>
    )
  }

  return (
    <div // NOSONAR
      className="w-full h-[160px] px-5 pb-5 pt-0"
      onKeyDown={handleKeyDown}
    >
      <RichTextEditor
        key={resetKey}
        value={editorValue}
        onChange={setEditorValue}
        maxCharacters={DEFAULT_MAX_CHARACTERS}
        autoFocus={false}
        disabled={isSubmitting || disabled}
        id={REPLY_EDITOR_ID}
        mode={EditorMode.PLAIN}
        placeholder={placeholder}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        showSendButton
        onSend={handleSend}
        isSending={isSubmitting}
        toolbarIconSize={14}
        className={cn('bg-white', isQuoted && 'rounded-t-none')}
      />
    </div>
  )
}
